import {Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import Select from 'react-select'
import {EdgeMemberPolicy, IResAllTagsDocuments} from '../../../../types/policiesApi'
import {useFormik} from 'formik'
import {ChangeEvent, useEffect, useState} from 'react'
import {useQuery} from '@apollo/client'
import {GET_DOCUMENTS_POLICY} from '../../../../gql/queries/policiesApiQueries'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {useUploadFile} from '../../../../hooks/useUploadFile'
import {ADD_DOCUMENT_POLICY_STR} from '../../../../gql/mutations/policyMutation'
import {parseId} from '../../../../helpers'
import {ProgressBar} from 'react-bootstrap'
import { DateInput } from 'app/tfi/components/DateInput'

const validationSchema = Yup.object().shape({
  name: Yup.string().when('attachments', {
    is: (attachments: { file: File; customName: string }[] | undefined) => !attachments || attachments.length <= 1,
    then: (schema) => schema.required('Name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  attachments: Yup.array()
    .of(
      Yup.object().shape({
        file: Yup.mixed<File>().test('fileType', 'Attachment must be a valid file', (file) =>
          file instanceof File
        ),
        customName: Yup.string(),
      })
    )
    .min(1, 'At least one file is required'),
})

const INIT_FORM = {
  name: '',
  attachments: [] as { file: File; customName: string }[],
  memberPolicyId: '',
  expirationDate: '',
  documentTagId: '',
  comments: '',
}

interface IResUploadFile {
  createAttachments: {success: boolean; errors?: {message: string}[]}
}

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  members: EdgeMemberPolicy[]
}
export const ModalAddDocsPolicy: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  members,
  id,
  refetch,
}) => {
  const [uploadFile, {data, error, loading}] = useUploadFile<IResUploadFile>()

  const [uploadProgress, setUploadProgress] = useState<{current: number; total: number}>({
    current: 0,
    total: 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  // previews: key=index, value=url
  const [previews, setPreviews] = useState<Record<number, string | null>>({})

  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'no-cache',
  })
  const formik = useFormik({
    initialValues: INIT_FORM,
    validationSchema,
    onSubmit: async (v) => {
      console.log("[ModalAddDocsPolicy] Submitting with values:", v)
      if (!v.attachments || v.attachments.length === 0) return
      console.log("[ModalAddDocsPolicy] Total files to upload:", v.attachments.length)
      setUploadProgress({current: 0, total: v.attachments.length})

      for (let i = 0; i < v.attachments.length; i++) {
        const { file, customName } = v.attachments[i]
        console.log("[ModalAddDocsPolicy] Uploading file:", file.name)
        const formdata = new FormData()
        formdata.append('0', file, file.name)
        const map = {'0': ['variables.attachmentsData.attachments']}
        const obj = {
          query: ADD_DOCUMENT_POLICY_STR,
          variables: {
            attachmentsData: {
              name: v.attachments.length === 1
                ? (v.name || customName || '').replace(/\.[^/.]+$/, '')
                : (customName || file.name).replace(/\.[^/.]+$/, ''),
              attachments: null,
              policyId: Number(id),
              memberPolicyId: v.memberPolicyId ? parseId(v.memberPolicyId) : undefined,
              expirationDate: v.expirationDate || undefined,
              documentTagId: v.documentTagId ? parseId(v.documentTagId) : undefined,
              comments: v.comments,
            },
          },
        }
        formdata.append('map', JSON.stringify(map))
        formdata.append('operations', JSON.stringify(obj))

        try {
          await uploadFile(formdata)
          console.log("[ModalAddDocsPolicy] Uploaded file:", file.name)
          setUploadProgress({current: i + 1, total: v.attachments.length})
        } catch (err) {
          console.error("[ModalAddDocsPolicy] Error uploading file:", file.name, err)
          toast.error(`Error uploading ${file.name}`)
        }
      }

      onClose()
      refetch()
      toast.success('Files uploaded successfully')
    },
  })

      // Generate previews for images in attachments
  useEffect(() => {
    const files = formik.values.attachments || []
    const nextPreviews: Record<number, string | null> = {}
    const urlsToRevoke: string[] = []
    files.forEach(({file}, idx) => {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file)
        nextPreviews[idx] = url
        urlsToRevoke.push(url)
      } else {
        nextPreviews[idx] = null
      }
    })
    setPreviews(nextPreviews)
    return () => {
      urlsToRevoke.forEach(url => URL.revokeObjectURL(url))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.attachments])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.toString()}`)
  }, [error])
  useEffect(() => {
    if (errorTags) toast.error(`Error: ${errorTags.message}`)
  }, [errorTags])

  // Permitir pegar archivos (Ctrl+V o Cmd+V)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return
      const files = Array.from(e.clipboardData.files || [])
      if (files.length > 0) {
        e.preventDefault()
        setIsDragging(true) // activa el highlight visual temporal

        const validFiles = files.filter((f) => f instanceof File).map(f => ({ file: f, customName: f.name.replace(/\.[^/.]+$/, '') }))
        if (validFiles.length > 0) {
          formik.setFieldValue('attachments', [
            ...formik.values.attachments,
            ...validFiles,
          ])

          if (validFiles.length === 1 && !formik.values.name) {
            formik.setFieldValue('name', validFiles[0].customName)
          } else if (validFiles.length !== 1) {
            formik.setFieldValue('name', '')
          }

          toast.info(`${validFiles.length} file(s) pasted`)
        }

        // desactiva el efecto visual después de 1 segundo
        setTimeout(() => setIsDragging(false), 1000)
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [formik])

  useEffect(() => {
    if (!data) return
    if (data.createAttachments.success) {
      onClose()
      refetch()
      toast.success('File uploaded successfully')
    } else if (data.createAttachments.errors && data.createAttachments.errors.length > 0) {
      toast.error(`Error: ${data.createAttachments.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  console.log("[ModalAddDocsPolicy] render form")
  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2'>
          Add Documents {loading && <Spinner animation='border' size='sm' />}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {uploadProgress.total > 0 && (
          <div className="mb-2">
            <span>
              Uploading {uploadProgress.current}/{uploadProgress.total}
            </span>
            <ProgressBar
              now={(uploadProgress.current / uploadProgress.total) * 100}
              label={`${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%`}
            />
          </div>
        )}
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className='my-3'>
                <Form.Label>
                  Document
                </Form.Label>
                <div
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setIsDragging(false)
                    const files = Array.from(e.dataTransfer.files || []).map(f => ({ file: f, customName: f.name.replace(/\.[^/.]+$/, '') }))
                    formik.setFieldValue('attachments', [...formik.values.attachments, ...files])
                    if (files.length === 1 && !formik.values.name) {
                      formik.setFieldValue('name', files[0].customName)
                    } else if (files.length !== 1) {
                      formik.setFieldValue('name', '')
                    }
                  }}
                  className={`dropzone border rounded p-3 text-center ${isDragging ? 'bg-light border-primary' : 'border-secondary'}`}
                  style={{cursor: 'pointer'}}
                >
                  {isDragging ? (
                    <span className="text-primary">Drop files here...</span>
                  ) : (
                    <span className="text-muted">Drag & drop files here, or click below to select</span>
                  )}
                </div>
                <Form.Control
                  name="attachments"
                  multiple
                  isInvalid={Boolean(formik.errors.attachments && formik.touched.attachments)}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const files = Array.from((e as ChangeEvent<HTMLInputElement>)?.target?.files || []).map(f => ({ file: f, customName: f.name.replace(/\.[^/.]+$/, '') }))
                    formik.setFieldValue('attachments', files)

                    if (files.length > 1) {
                      formik.setFieldValue('name', '') // oculta campo name
                    } else if (files.length === 1 && !formik.values.name) {
                      formik.setFieldValue('name', files[0].customName)
                    }
                  }}
                  type="file"
                />
                {formik.values.attachments && formik.values.attachments.length > 0 && (
                  <div className="d-flex flex-wrap gap-3 mt-2">
                    {formik.values.attachments.map(({ file, customName }, index) => {
                      const fileNameWithoutExt = customName
                      const isImage = file.type.startsWith('image/')
                      const previewUrl = previews[index]
                      return (
                        <div key={index} className="border rounded p-2 d-flex align-items-center gap-2" style={{minWidth: 150}}>
                          {isImage && previewUrl ? (
                            <img src={previewUrl} alt={file.name} width={40} height={40} style={{objectFit: 'cover', borderRadius: 4}} />
                          ) : (
                            <div style={{width: 40, height: 40, backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4}}>
                              <span className="text-muted" style={{fontSize: 18}}>📄</span>
                            </div>
                          )}
                          <Form.Control
                            type="text"
                            value={customName}
                            onChange={(e) => {
                              const newAttachments = [...formik.values.attachments]
                              newAttachments[index] = { file, customName: e.target.value }
                              formik.setFieldValue('attachments', newAttachments)
                              if (newAttachments.length === 1 && !formik.values.name) {
                                formik.setFieldValue('name', e.target.value)
                              } else if (newAttachments.length !== 1) {
                                formik.setFieldValue('name', '')
                              }
                            }}
                            placeholder="File name"
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            className="p-0 text-white"
                            onClick={() => {
                              const newFiles = [...formik.values.attachments]
                              newFiles.splice(index, 1)
                              formik.setFieldValue('attachments', newFiles)
                              if (newFiles.length === 1 && !formik.values.name) {
                                formik.setFieldValue('name', newFiles[0].customName)
                              } else if (newFiles.length !== 1) {
                                formik.setFieldValue('name', '')
                              }
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}
                {formik.touched.attachments && formik.errors.attachments && (
                  <Form.Text className='text-danger'>
                    {typeof formik.errors.attachments === 'string' ? formik.errors.attachments : 'Please add at least one valid file.'}
                  </Form.Text>
                )}
                <Form.Text className="text-muted">
                  You can select one or more files to upload.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  isInvalid={Boolean(formik.errors.name && formik.touched.name)}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  name='name'
                  id='name'
                  placeholder='File name'
                  disabled={formik.values.attachments && formik.values.attachments.length > 1}
                />
                {formik.errors.name && formik.touched.name && (
                  <Form.Text className='text-danger'>{formik.errors.name}</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <DateInput
                id="expirationDate"
                name="expirationDate"
                label="Expiration"
                value={formik.values.expirationDate}
                error={formik.errors.expirationDate}
                touched={formik.touched.expirationDate}
                onChange={(field, value) => formik.setFieldValue(field, value)}
                onBlur={formik.handleBlur}
              />
            </Col>
            <Col md={6}>
              <Form.Group className='my-3'>
                <Form.Label>Member</Form.Label>
                <Select
                  name='memberPolicyId'
                  onBlur={formik.handleBlur}
                  isClearable
                  onChange={(e) => {
                    if (e) {
                      formik.setFieldValue('memberPolicyId', e.value)
                    } else {
                      formik.setFieldValue('memberPolicyId', '')
                    }
                  }}
                  options={members.map(({node}) => ({
                    label: `${node.firstName} ${node.lastName}`,
                    value: node.id,
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId='select2' className='my-3'>
                <Form.Label>Tag</Form.Label>
                <Select
                  isClearable
                  onChange={(e) => {
                    if (e) {
                      formik.setFieldValue('documentTagId', e.value)
                    } else {
                      formik.setFieldValue('documentTagId', '')
                    }
                  }}
                  options={
                    dataTags?.allAttachmentsTags.edges.map(({node}) => ({
                      label: `${node.tagName}}`,
                      value: node.id,
                    })) || []
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className='mt-3'>
            <Form.Label>Comment</Form.Label>
            <Form.Control
              name='comments'
              onChange={formik.handleChange}
              value={formik.values.comments}
              as='textarea'
              rows={3}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} type='button'>
          Close
        </Button>
        <Button variant='primary' type='submit' disabled={loading || loadingTags} onClick={() => formik.handleSubmit()}>
          Upload Files
        </Button>
      </Modal.Footer>
    </Modal>
  )
}